import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefundReviewItem, RefundReviewService } from '../services/refund-review.service';

type RefundReviewRow = Omit<RefundReviewItem, 'amount' | 'booking'> & {
  amount: number;
  booking: Omit<RefundReviewItem['booking'], 'totalAmount'> & { totalAmount: number };
  receiptUrl: string;
  transferDate: Date | null;
  transferTime: Date | null;
};

@Component({
  selector: 'app-refund-review-page',
  imports: [
    DatePipe,
    DecimalPipe,
    FormsModule,
    LucideAngularModule,
    DatePickerModule,
    InputTextModule,
    TableModule,
    RefreshButtonComponent,
    StatusBadge,
  ],
  templateUrl: './refund-review-page.component.html',
})
export class RefundReviewPageComponent implements OnInit {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly service = inject(RefundReviewService);

  readonly rows = signal<RefundReviewRow[]>([]);
  readonly selected = signal<RefundReviewRow | null>(null);
  readonly isLoading = signal(false);
  readonly receiptUrlError = signal('');
  readonly transferDateTimeError = signal('');

  ngOnInit(): void {
    void this.loadRefunds();
  }

  async loadRefunds(): Promise<void> {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.overlay.showLoading('กำลังโหลดรายการคืนเงินรอตรวจสอบ...');
    try {
      const refunds = await this.service.getPending();
      this.rows.set(
        refunds.map((refund) => ({
          ...refund,
          amount: Number(refund.amount),
          booking: { ...refund.booking, totalAmount: Number(refund.booking.totalAmount) },
          receiptUrl: '',
          transferDate: null,
          transferTime: null,
        })),
      );
    } catch (error: unknown) {
      this.showError('โหลดรายการคืนเงินไม่สำเร็จ', error);
    } finally {
      this.isLoading.set(false);
      this.overlay.hideLoading();
    }
  }

  open(row: RefundReviewRow): void {
    this.receiptUrlError.set('');
    this.transferDateTimeError.set('');
    if (!row.transferDate || !row.transferTime) {
      const now = new Date();
      row.transferDate = new Date(now);
      row.transferTime = new Date(now);
    }
    this.selected.set(row);
  }

  close(): void {
    this.selected.set(null);
  }

  openAttachment(path: string | null): void {
    if (path) window.open(this.service.resolveFileUrl(path), '_blank', 'noopener,noreferrer');
  }

  clearReceiptUrlError(): void {
    this.receiptUrlError.set('');
  }

  clearTransferDateTimeError(): void {
    this.transferDateTimeError.set('');
  }

  async approve(row: RefundReviewRow): Promise<void> {
    const receiptUrlError = this.validateReceiptUrl(row.receiptUrl);
    this.receiptUrlError.set(receiptUrlError);
    if (!row.transferDate || !row.transferTime) {
      this.transferDateTimeError.set('กรุณาระบุวันที่และเวลาโอนเงิน');
      return;
    }
    this.transferDateTimeError.set('');
    if (receiptUrlError) return;

    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการคืนเงิน',
      message: `ยืนยันโอนคืน ${row.refundNo} จำนวน ${row.amount.toLocaleString()} บาท หรือไม่`,
      confirmText: 'ยืนยันการโอน',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังบันทึกการคืนเงิน...');
    try {
      await this.service.approve(
        row.id,
        row.receiptUrl.trim(),
        this.toDateValue(row.transferDate),
        this.toTimeValue(row.transferTime),
      );
      this.remove(row.id);
      this.overlay.success('บันทึกการคืนเงินสำเร็จ', `${row.refundNo} ถูกบันทึกเป็นจ่ายแล้ว`);
    } catch (error: unknown) {
      this.showError('บันทึกการคืนเงินไม่สำเร็จ', error);
    } finally {
      this.overlay.hideLoading();
    }
  }

  async reject(row: RefundReviewRow): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'ไม่อนุมัติการคืนเงิน',
      message: `ยืนยันไม่อนุมัติ ${row.refundNo} หรือไม่`,
      confirmText: 'ไม่อนุมัติ',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังบันทึกผลการตรวจสอบ...');
    try {
      await this.service.reject(row.id);
      this.remove(row.id);
      this.overlay.success('บันทึกผลแล้ว', `${row.refundNo} ไม่ผ่านการตรวจสอบ`);
    } catch (error: unknown) {
      this.showError('บันทึกผลไม่สำเร็จ', error);
    } finally {
      this.overlay.hideLoading();
    }
  }

  private remove(id: string): void {
    this.rows.update((rows) => rows.filter((row) => row.id !== id));
    this.close();
  }

  private validateReceiptUrl(value: string): string {
    if (!value.trim()) return 'กรุณากรอกลิงก์หลักฐานการโอน';
    try {
      const url = new URL(value.trim());
      return url.protocol === 'http:' || url.protocol === 'https:'
        ? ''
        : 'ลิงก์หลักฐานการโอนต้องขึ้นต้นด้วย http:// หรือ https://';
    } catch {
      return 'รูปแบบลิงก์หลักฐานการโอนไม่ถูกต้อง';
    }
  }

  private toDateValue(value: Date): string {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  }

  private toTimeValue(value: Date): string {
    return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;
  }

  private showError(title: string, error: unknown): void {
    const message =
      error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
        ? error.error.message
        : 'กรุณาลองใหม่อีกครั้ง';
    this.overlay.error(title, message);
  }
}
